import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../api';

/**
 * Bulk catalog deliveries (tcvs #119). The machine client submits deliveries; this view is the only place one
 * is approved or resumed, under the signed-in owner's identity and bound to the exact preview report digest.
 */
export interface CatalogDeliverySummary {
  deliveryId: string;
  status: 'PREVIEWING' | 'AWAITING_APPROVAL' | 'APPLYING' | 'STOPPED' | 'COMPLETED';
  total?: number;
  counts?: Partial<
    Record<
      'previewed' | 'ready' | 'unchanged' | 'blocked' | 'done' | 'verified' | 'stale' | 'failed',
      number
    >
  >;
  reportSha256?: string;
  reportBytes?: number;
  resultSha256?: string;
  submittedAt?: string;
  submittedBy?: string;
  approvedAt?: string;
  approvedBy?: string;
  stopReason?: string;
  previewReportUrl?: string;
  resultReportUrl?: string;
}

const ROUTE = '/checklists/source-ingest';
const n = (value: number | undefined) => value ?? 0;

export function CatalogDeliveriesView(): JSX.Element {
  const api = useApi();
  const [deliveries, setDeliveries] = useState<CatalogDeliverySummary[]>([]);
  const [selected, setSelected] = useState<CatalogDeliverySummary | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const result = await api.request<{ deliveries: CatalogDeliverySummary[] }>(ROUTE, {
      method: 'POST',
      body: { operation: 'DELIVERY_LIST' },
    });
    if (result.ok) setDeliveries(result.data.deliveries);
    else setError(result.message ?? result.error);
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = async (deliveryId: string) => {
    setError(null);
    setReviewed(false);
    const result = await api.request<CatalogDeliverySummary>(ROUTE, {
      method: 'POST',
      body: { operation: 'DELIVERY_STATUS', deliveryId },
    });
    if (result.ok) setSelected(result.data);
    else setError(result.message ?? result.error);
  };

  const act = async (operation: 'DELIVERY_APPROVE' | 'DELIVERY_RESUME') => {
    if (selected === null || busy) return;
    setBusy(true);
    setError(null);
    const result = await api.request<CatalogDeliverySummary>(ROUTE, {
      method: 'POST',
      body:
        operation === 'DELIVERY_APPROVE'
          ? { operation, deliveryId: selected.deliveryId, reportSha256: selected.reportSha256 }
          : { operation, deliveryId: selected.deliveryId },
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    await open(selected.deliveryId);
    await load();
  };

  return (
    <div className="tcvs-catalog-deliveries">
      <p>
        Catalog deliveries are submitted by the catalog pipeline and previewed by TCVS in the
        background. Nothing changes until you approve the exact preview report below.
      </p>
      {error !== null && <p role="alert">{error}</p>}
      <table>
        <thead>
          <tr>
            <th>Submitted</th>
            <th>Status</th>
            <th>Products</th>
            <th>To apply</th>
            <th>Unchanged</th>
            <th>Blocked</th>
            <th>Verified</th>
            <th>Stale / failed</th>
          </tr>
        </thead>
        <tbody>
          {deliveries.map((d) => (
            <tr
              key={d.deliveryId}
              aria-selected={selected?.deliveryId === d.deliveryId}
              onClick={() => void open(d.deliveryId)}
              style={{ cursor: 'pointer' }}
            >
              <td>{d.submittedAt?.replace('T', ' ').slice(0, 16) ?? ''}</td>
              <td>{d.status}</td>
              <td>{n(d.total)}</td>
              <td>{n(d.counts?.ready)}</td>
              <td>{n(d.counts?.unchanged)}</td>
              <td>{n(d.counts?.blocked)}</td>
              <td>{n(d.counts?.verified)}</td>
              <td>
                {n(d.counts?.stale)} / {n(d.counts?.failed)}
              </td>
            </tr>
          ))}
          {deliveries.length === 0 && (
            <tr>
              <td colSpan={8}>No deliveries yet.</td>
            </tr>
          )}
        </tbody>
      </table>

      {selected !== null && (
        <section aria-label="Delivery detail">
          <h3>Delivery {selected.deliveryId.slice(0, 8)}</h3>
          <p>
            {n(selected.counts?.ready)} products to apply, {n(selected.counts?.unchanged)}{' '}
            unchanged, {n(selected.counts?.blocked)} blocked (never applied).
          </p>
          {selected.previewReportUrl && (
            <p>
              <a href={selected.previewReportUrl} target="_blank" rel="noreferrer">
                Download preview report
              </a>{' '}
              (SHA-256 <code>{selected.reportSha256?.slice(0, 16)}…</code>)
            </p>
          )}
          {selected.resultReportUrl && (
            <p>
              <a href={selected.resultReportUrl} target="_blank" rel="noreferrer">
                Download result report
              </a>
            </p>
          )}
          {selected.approvedBy && (
            <p>
              Approved by {selected.approvedBy} at {selected.approvedAt}
            </p>
          )}
          {selected.status === 'AWAITING_APPROVAL' && (
            <div>
              <label>
                <input
                  type="checkbox"
                  checked={reviewed}
                  onChange={(event) => setReviewed(event.target.checked)}
                />{' '}
                I reviewed this preview report
              </label>{' '}
              <button
                type="button"
                disabled={!reviewed || busy}
                onClick={() => void act('DELIVERY_APPROVE')}
              >
                Approve and apply {n(selected.counts?.ready)} products
              </button>
            </div>
          )}
          {selected.status === 'STOPPED' && (
            <div>
              <p role="alert">Stopped: {selected.stopReason}</p>
              <button type="button" disabled={busy} onClick={() => void act('DELIVERY_RESUME')}>
                Resume delivery
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
